import { classNames } from '@dimjs/utils';
import { animated, useSpring } from '@react-spring/web';
import { hooks } from '@wove/react';
import { CSSProperties, FC, Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mask } from '../mask';
import { GetContainer, resolveContainer } from '../utils/get-container';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { getZIndex } from '../utils/z-index';
import './style.less';
import { useShouldRender } from './utils/use-should-render';

const classPrefix = `v-modal-drawer`;
export type ModalDrawerProps = {
  visible?: boolean;
  mask?: boolean;
  onMaskClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  getContainer?: GetContainer;
  onBeforeOpen?: () => void;
  onAfterOpen?: () => void;
  onBeforeClose?: () => void;
  onAfterClose?: () => void;
  position?: 'bottom' | 'top' | 'left' | 'right';
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  maskClassName?: string;
  maskStyle?: CSSProperties;
  closeByMask?: boolean;
  onClose?: (operate?: 'mask' | 'closeIcon') => void;
  inline?: boolean;
  closeIcon?: boolean;
  title?: string | JSX.Element;
  bodyRef?: (node: HTMLDivElement) => void;
  duration?: number;
} & CommonPropsWithChildren<{ '--modal-border-radius': CSSProperties['borderRadius'] }>;

const ModalDrawerContent: FC<ModalDrawerProps> = (props) => {
  return (
    <Fragment>
      {props.closeIcon && (
        <div className={`${classPrefix}-body-close`} onClick={props.onClose?.bind(null, 'closeIcon')}></div>
      )}
      {props.title && <div className={`${classPrefix}-body-title`}>{props.title}</div>}
      {props.children}
    </Fragment>
  );
};

export const ModalDrawer: FC<ModalDrawerProps> = (props) => {
  const getContainer = props.inline ? null : props.getContainer;
  const maskZIndex = useRef<number>();
  const bodyZIndex = useRef<number>();
  const bodyCls = classNames(
    `${classPrefix}-body`,
    props.bodyClassName,
    `${classPrefix}-body-${props.position}`,
  );
  const updateZindex = hooks.useCallbackRef(() => {
    maskZIndex.current = getZIndex();
    bodyZIndex.current = getZIndex();
  });

  const [visible, setVisible] = useState(() => {
    if (props.visible) {
      updateZindex();
    }
    return props.visible || false;
  });
  const shouldRender = useShouldRender(visible, props.forceRender, props.destroyOnClose);

  const onBeforeOpenHandle = hooks.useCallbackRef(() => {
    updateZindex();
    props.onBeforeOpen?.();
    setVisible(true);
  });

  const onAfterOpenHandle = hooks.useCallbackRef(() => {
    props.onAfterOpen?.();
  });

  const onBeforeCloseHandle = hooks.useCallbackRef(() => {
    props.onBeforeClose?.();
  });

  const onAfterCloseHandle = hooks.useCallbackRef(() => {
    setVisible(false);
    props.onAfterClose?.();
  });

  const { percent } = useSpring({
    percent: props.visible ? 0 : 100,
    config: {
      precision: 0.1,
      mass: 0.4,
      tension: 300,
      friction: 30,
      duration: props.duration,
    },
    onStart: () => {
      if (!visible) {
        onBeforeOpenHandle();
      } else {
        onBeforeCloseHandle();
      }
    },
    onRest: () => {
      if (props.visible) {
        onAfterOpenHandle();
      } else {
        onAfterCloseHandle();
      }
    },
  });

  const onClick = hooks.useCallbackRef((event) => {
    event.stopPropagation();
  });

  const onMaskClick = hooks.useCallbackRef((event) => {
    event.stopPropagation();
    if (props.onMaskClick) {
      props.onMaskClick(event as React.MouseEvent<HTMLDivElement, MouseEvent>);
    } else {
      if (props.closeByMask) {
        props.onClose?.('mask');
      }
    }
  });

  if (!visible) {
    document.body.classList.remove('overflow-hidden');
  } else {
    document.body.classList.add('overflow-hidden');
  }

  const node = withNativeProps(
    props,
    <div
      className={classNames(classPrefix, {
        [`${classPrefix}-inline`]: props.inline,
        [`${classPrefix}-open`]: visible,
      })}
      onClick={onClick}
      style={{ display: visible ? 'unset' : 'none' }}
    >
      <div>
        {props.mask && (
          <Mask
            visible={props.visible}
            onMaskClick={onMaskClick}
            className={props.maskClassName}
            style={{ zIndex: maskZIndex.current, ...props.maskStyle }}
          />
        )}
        <animated.div
          className={bodyCls}
          style={{
            ...props.bodyStyle,
            transform: percent.to((v) => {
              if (props.position === 'bottom') {
                return `translate(0, ${v}%)`;
              }
              if (props.position === 'top') {
                return `translate(0, -${v}%)`;
              }
              if (props.position === 'left') {
                return `translate(-${v}%, 0)`;
              }
              if (props.position === 'right') {
                return `translate(${v}%, 0)`;
              }
              return 'none';
            }),
            zIndex: bodyZIndex.current,
          }}
          ref={props.bodyRef}
        >
          {shouldRender && <ModalDrawerContent {...props} />}
        </animated.div>
      </div>
    </div>,
  );
  if (getContainer) {
    const container = resolveContainer(getContainer);
    return createPortal(node, container);
  } else {
    return node;
  }
};

ModalDrawer.defaultProps = {
  position: 'bottom',
  visible: false,
  getContainer: () => document.body,
  mask: true,
  closeByMask: true,
};
